/*
 * Endpoint hooks both V2 trees call. Thin `useFetch` wrappers (code standard §5);
 * the JSDoc is the contract summary, the contract files in EpicenterApi
 * `docs/batches-revamp/spec/08-contracts/` are the truth.
 */

import useFetch from "../../../../hooks/useFetch";

/**
 * Verify a student: their details, the account owner, the purchases' figures, and
 * every course on an active order without a running batch (eligible and blocked),
 * each with its count ceiling, preferences, continuations and stitch targets.
 * GET /batch-flow/verify?email= (E1)
 * Returns: { student, manager, purchases[], courses[] }
 * Refuses: email-not-found, no-orders, no-active-order, all-courses-have-batch,
 *   all-courses-blocked, no-account-owner, student-paused, invalid-request.
 * @param {Object} [options] - `manual: true` by default; fire via mutate({ params: { email } }).
 *   Edit reads the course list for its new stitch batch with `manual: false, params`.
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useVerifyStudent = (options = {}) =>
  useFetch("/batch-flow/verify", { method: "get", manual: true, ...options });

/**
 * The mentor list for a schedule: the known mentor, the best-matched five and the
 * search pool, or one picked mentor's full card.
 * POST /batch-flow/mentors (E2)
 * Takes: { studentId, orderId, courseId, schedule: { startDate, timings, sessionCount },
 *          batchId, mode: "list"|"search"|"check", term, mentorId } — build it with
 *          `buildMentorListRequest`.
 * Returns: { preselectedOption, studentTimezoneLabel, known, bestMatched[], search[] }
 * Refuses (400 envelope): invalid-request, section 4's schedule reasons, and in check
 *   mode mentor-not-usable. Never refuses on availability — it answers states.
 * @param {Object} [options] - `manual: true` by default; fire via mutate({ data })
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchMentorList = (options = {}) =>
  useFetch("/batch-flow/mentors", { method: "post", manual: true, ...options });

/**
 * Every projected date and count the two screens show, answered from one lay-out
 * (E3). The screens compute none of them: the completion date, the regular/extra
 * split, a flex batch's count and validity end, and the validity comparison all
 * come from here (R-04-06, decided 2026-09-24).
 *
 * POST /batch-flow/preview
 * Takes: { kind: "credit"|"flex",
 *          schedule: { startDate, timings, sessionCount },
 *          figures: { sessionCeilingParts, validityEnd, durationMonths,
 *                     weeklyFrequency, flexAnchor,
 *                     classesPerWeek },                // E1 or E5, sent back unchanged
 *          batch }                                     // E5's block; its presence means edit
 * Returns: { regularEnd, finalEnd, split: { regular, extra },
 *            flex: { count, validityEnd }|null,
 *            validity: { overrun, projectedEnd, validityEnd }|null,
 *            classesPerWeek: { permitted, scheduled, afterChange } }  // when sent
 * Every date is "YYYY-MM-DD". It reads no database and answers no warning — the
 * validity overrun comes back as data, and again as an acceptable warning at the
 * write. A successor is never previewed: E2 answers its window.
 * @param {Object} [options] - `manual: true` by default; fire via mutate({ data })
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchPreview = (options = {}) =>
  useFetch("/batch-flow/preview", { method: "post", manual: true, ...options });
